/**
 * Utility functions for converting between candidate products and cart payloads.
 */
import { CandidateProduct } from "modules/lists/types/lists";
import { AddCartMethod } from "modules/cart/types/add-cart-methods";
import { ProductData } from "modules/product/types/product";

import { CartItem, Payload } from "modules/cart/types/cart";

/**
 * Transforms a candidate product into a cart payload for addDeletefromCart.
 * @param candidate - The candidate product from the resolution.
 * @param listItemId - The ID of the list item this candidate resolves.
 * @param quantity - Desired quantity.
 * @returns Payload suitable for addDeletefromCart.
 */
export const payloadFromCandidate = (
  candidate: CandidateProduct,
  listItemId: string,
  quantity: number,
): Payload => ({
  id: candidate.sku ?? String(candidate.catalogProductId),
  sku: candidate.sku ?? String(candidate.catalogProductId),
  name: candidate.name,
  category_id: 0, // Will be resolved by getPrice
  quantity,
  method: "SAME" as AddCartMethod,
});

/**
 * Transforms a single AI-resolver candidate into the ProductData shape used
 * by the alternatives carousel and the replacement preferences dialog.
 * The candidate's hashed SKU becomes the best_price_id so that
 * `ProductDecision.replacementIds` remain stable across the cart sheet.
 * @param candidate - The candidate product from the resolution.
 * @returns A ProductData-compatible alternative.
 */
export const candidateToProductData = (candidate: CandidateProduct): ProductData => {
  const sku = candidate.sku ?? null;
  const price = candidate.price ?? 0;
  const productId = sku ?? candidate.id;
  // Prefer the structured image set (thumbs/catalog/detail); fall back to the
  // legacy single imageUrl for backward compatibility.
  const imageUrl = candidate.images?.catalog ?? candidate.imageUrl ?? "";
  return {
    id: candidate.id,
    brand: candidate.brand,
    category_id: 0,
    slug: candidate.slug,
    price_detail: {
      best_price_id: productId,
      worst_price_id: productId,
      legacy_price: 0,
      best_price: price,
      worst_price: price,
    },
    stores: [
      {
        name: candidate.name,
        normalized_name: candidate.name,
        description: "",
        available: 1,
        image_url: imageUrl,
        images: {
          thumbs: candidate.images?.thumbs ?? "",
          catalog: candidate.images?.catalog ?? imageUrl,
          detail: candidate.images?.detail ?? "",
        },
        price,
        price_per_unit: 0,
        price_per_unit_scale: 1,
        show_pper_unit: false,
        promos: [],
        store_id: candidate.catalogProductId,
        sku: sku ?? "",
        url: "",
        s_id: candidate.catalogProductId,
      },
    ],
    hashed_sku: sku,
  };
};

/**
 * Transforms a list of candidates into ProductData alternatives.
 * @param candidates - The candidates from the resolution.
 * @returns ProductData-compatible alternatives.
 */
export const candidatesToProductData = (candidates: CandidateProduct[]): ProductData[] =>
  candidates.map(candidateToProductData);

/**
 * Demotes a resolved cart item into a CandidateProduct so it can be listed
 * as an alternative after a swap. Fields not available in the cart item
 * (catalogProductId) are defaulted to 0; sku/price/images carry over.
 * @param item - The cart item being demoted.
 * @returns A CandidateProduct-compatible alternative.
 */
export const candidateFromCartItem = (item: CartItem): CandidateProduct => {
  const pc = item.price_component[0];
  const quantity = pc?.quantity ?? 1;
  const unitPrice =
    pc && quantity > 0 ? pc.price / quantity : (pc?.price ?? null);
  return {
    id: item.id,
    catalogProductId: 0,
    canonicalSku: item.id,
    slug: item.slug,
    name: pc?.name ?? item.name,
    brand: item.brand,
    imageUrl: pc?.image_url ?? null,
    images: pc?.images ?? null,
    price: unitPrice,
    unit: null,
    confidenceScore: 0,
    rank: 0,
    selected: false,
    sku: pc?.sku ?? null,
  };
};

